import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { styles } from './ui.styles';
import { usePermission } from '@/core/auth/usePermission';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════
export interface Column<T> {
  key:       keyof T | string;
  label:     string;
  width?:    string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?:   (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
}

export interface PaginationMeta {
  total:       number;
  page:        number;
  page_size:   number;
  total_pages: number;
}

// ═══════════════════════════════════════════════════════════════════
// MODAL
// ═══════════════════════════════════════════════════════════════════
interface ModalProps {
  isOpen:    boolean;
  onClose:   () => void;
  title:     string;
  size?:     'sm' | 'md' | 'lg' | 'xl';
  children:  React.ReactNode;
  footer?:   React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, size = 'md', children, footer }) => {
  const widths = { sm: 420, md: 560, lg: 720, xl: 960 };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handler);
    }
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handler);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={styles.modalOverlay}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
      role="presentation"
    >
      <div style={styles.modalCard(widths[size])}>
        {/* Header */}
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={styles.modalCloseBtn}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#F8F7FA'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#FFFFFF'; }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={styles.modalBody}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div style={styles.modalFooter}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};


// ═══════════════════════════════════════════════════════════════════
// CONFIRM DIALOG
// ═══════════════════════════════════════════════════════════════════
interface ConfirmDialogProps {
  isOpen:         boolean;
  onClose:        () => void;
  onConfirm:      () => void;
  title?:         string;
  message:        string;
  confirmLabel?:  string;
  confirmVariant?: 'danger' | 'primary';
  loading?:       boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen, onClose, onConfirm, title = 'Confirm Action',
  message, confirmLabel = 'Confirm', confirmVariant = 'danger', loading,
}) => (
  <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm"
    footer={
      <>
        <button className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
        <button
          className={`btn btn-${confirmVariant}`}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? <span className="spinner" /> : confirmLabel}
        </button>
      </>
    }
  >
    <p style={styles.confirmMessage}>
      {message}
    </p>
  </Modal>
);

// ═══════════════════════════════════════════════════════════════════
// FORM FIELD
// ═══════════════════════════════════════════════════════════════════
interface FormFieldProps {
  label:        string;
  required?:    boolean;
  error?:       string;
  hint?:        string;
  children:     React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({ label, required, error, hint, children }) => (
  <div className="form-group">
    <label className="form-label">
      {label}
      {required && <span style={styles.asterisk}>*</span>}
    </label>
    {children}
    {error && <span className="form-error">{error}</span>}
    {hint && !error && <span className="form-hint">{hint}</span>}
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// SELECT DROPDOWN
// ═══════════════════════════════════════════════════════════════════
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  options: { value: string; label: string }[];
  placeholder?: string;
}

export const Select: React.FC<SelectProps> = ({ options, placeholder, ...props }) => (
  <select
    className="form-input"
    style={styles.selectPointer}
    {...props}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

// ═══════════════════════════════════════════════════════════════════
// TOAST NOTIFICATION
// ═══════════════════════════════════════════════════════════════════
type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id:      string;
  type:    ToastType;
  message: string;
}

const toastListeners: ((toast: Omit<Toast, 'id'>) => void)[] = [];

export const toast = {
  success: (msg: string) => toastListeners.forEach(l => l({ type: 'success', message: msg })),
  error:   (msg: string) => toastListeners.forEach(l => l({ type: 'error',   message: msg })),
  warning: (msg: string) => toastListeners.forEach(l => l({ type: 'warning', message: msg })),
  info:    (msg: string) => toastListeners.forEach(l => l({ type: 'info',    message: msg })),
};

const TOAST_COLORS: Record<ToastType, { bg: string; border: string; icon: string }> = {
  success: { bg: '#E6F9EE', border: '#28C76F', icon: '✓' },
  error:   { bg: '#FDEAEA', border: '#EA5455', icon: '✕' },
  warning: { bg: '#FFF3E5', border: '#FF9F43', icon: '⚠' },
  info:    { bg: '#E0F9FC', border: '#00CFE8', icon: 'ℹ' },
};

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const listener = (t: Omit<Toast, 'id'>) => {
      const id = Date.now().toString();
      setToasts(prev => [...prev, { ...t, id }]);
      setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 4000);
    };
    toastListeners.push(listener);
    return () => { const i = toastListeners.indexOf(listener); if (i > -1) toastListeners.splice(i, 1); };
  }, []);

  return (
    <div style={styles.toastContainer}>
      {toasts.map(t => {
        const c = TOAST_COLORS[t.type];
        return (
          <div key={t.id} style={styles.toastItem(c.bg, c.border)}>
            <span style={styles.toastIcon}>{c.icon}</span>
            <span style={styles.toastMessage}>
              {t.message}
            </span>
            <button
              onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
              style={styles.toastCloseBtn}
            >×</button>
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// DATA TABLE with Pagination
// ═══════════════════════════════════════════════════════════════════
interface DataTableProps<T> {
  columns:      Column<T>[];
  data:         T[];
  loading?:     boolean;
  meta?:        PaginationMeta;
  onPageChange?: (page: number) => void;
  emptyMessage?: string;
  emptyIcon?:   React.ReactNode;
}

export function DataTable<T extends { _id?: string }>({
  columns, data, loading, meta, onPageChange, emptyMessage = 'No records found', emptyIcon,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <div style={styles.tableLoadingWrapper}>
        {[0,1,2,3,4].map(i => (
          <div key={i} style={styles.tableLoadingRow}>
            <div className="skeleton" style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div className="skeleton" style={{ height: 13, width: '40%', marginBottom: 6 }} />
              <div className="skeleton" style={{ height: 11, width: '25%' }} />
            </div>
            <div className="skeleton" style={{ height: 24, width: 70, borderRadius: 99 }} />
            <div className="skeleton" style={{ height: 28, width: 80 }} />
          </div>
        ))}
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#9092A9', marginBottom: 12 }}>
          {emptyIcon || (
            <svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          )}
        </div>
        <div className="empty-state-title" style={{ fontSize: '1.1rem', fontWeight: 600, color: '#323450', marginBottom: 6 }}>No Records Found</div>
        <div className="empty-state-text" style={{ color: 'var(--clr-text-muted)', fontSize: '0.85rem' }}>{emptyMessage}</div>
      </div>
    );
  }

  return (
    <>
      <div style={styles.tableOverflowWrapper}>
        <table className="data-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key as string} style={{ width: col.width }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={(row as { _id?: string })._id || i}>
                {columns.map(col => {
                  const val = (row as Record<string, unknown>)[col.key as string];
                  return (
                    <td key={col.key as string}>
                      {col.render ? col.render(val, row) : String(val ?? '—')}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta && meta.total_pages > 1 && (
        <div style={styles.paginationWrapper}>
          <span>
            Showing <strong style={styles.paginationCountBold}>
              {Math.min(meta.page_size, meta.total - (meta.page - 1) * meta.page_size)}
            </strong> of <strong style={styles.paginationCountBold}>{meta.total}</strong> records
          </span>
          <div style={styles.paginationGroup}>
            <button
              className="icon-btn"
              disabled={meta.page <= 1}
              onClick={() => onPageChange?.(meta.page - 1)}
              style={{ opacity: meta.page <= 1 ? 0.4 : 1 }}
            >‹</button>
            {Array.from({ length: Math.min(meta.total_pages, 5) }, (_, i) => {
              const p = Math.max(1, Math.min(meta.page - 2, meta.total_pages - 4)) + i;
              return (
                <button
                  key={p}
                  onClick={() => onPageChange?.(p)}
                  style={styles.paginationBtn(meta.page === p)}
                >{p}</button>
              );
            })}
            <button
              className="icon-btn"
              disabled={meta.page >= meta.total_pages}
              onClick={() => onPageChange?.(meta.page + 1)}
              style={{ opacity: meta.page >= meta.total_pages ? 0.4 : 1 }}
            >›</button>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// STATUS BADGE
// ═══════════════════════════════════════════════════════════════════
const STATUS_MAP: Record<string, string> = {
  active:      'badge-success',
  pending:     'badge-warning',
  suspended:   'badge-danger',
  deactivated: 'badge-neutral',
  inactive:    'badge-neutral',
  true:        'badge-success',
  false:       'badge-danger',
};

export const StatusBadge: React.FC<{ status: string | boolean }> = ({ status }) => {
  const key = String(status).toLowerCase();
  const cls = STATUS_MAP[key] || 'badge-neutral';
  const label = typeof status === 'boolean'
    ? (status ? 'Active' : 'Inactive')
    : status.charAt(0).toUpperCase() + status.slice(1);
  return <span className={`badge ${cls}`}>{label}</span>;
};

// ═══════════════════════════════════════════════════════════════════
// PAGE HEADER
// ═══════════════════════════════════════════════════════════════════
interface PageHeaderProps {
  title:       string;
  subtitle?:   string;
  actions?:    React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, actions }) => (
  <div className="page-header">
    <div>
      <h1 className="page-title">{title}</h1>
      {subtitle && <p className="page-subtitle">{subtitle}</p>}
    </div>
    {actions && <div style={styles.pageHeaderActionsGroup}>{actions}</div>}
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// CRUD PAGE WRAPPER (reusable pattern for all catalog modules)
// ═══════════════════════════════════════════════════════════════════
interface CrudPageProps<T> {
  title:           string;
  subtitle:        string;
  icon?:           React.ReactNode;
  data:            T[];
  loading:         boolean;
  meta?:           PaginationMeta | null;
  onSearch:        (query: string) => void;
  onPageChange:    (page: number) => void;
  onCreate?:       (formData: Partial<T>) => Promise<boolean>;
  onUpdate?:       (id: string, formData: Partial<T>) => Promise<boolean>;
  onDelete:        (id: string) => Promise<boolean>;
  columns:         Column<T>[];
  renderForm?:     (formData: Partial<T>, onChange: (field: string, value: unknown) => void, mode: 'create' | 'edit') => React.ReactNode;
  defaultForm?:    Partial<T>;
  searchable?:     boolean;
  extraFilters?:   React.ReactNode;
  emptyIcon?:      React.ReactNode;
  emptyMessage?:   string;
  onCreateClick?:  () => void;
  onEditClick?:    (item: T) => void;
  onViewClick?:    (item: T) => void;
  actions?:        React.ReactNode;
  addLabel?:       string;
  page?:           number;
  totalPages?:     number;
  totalRecords?:   number;
  /** Permission module (e.g. "students"): hides Add / Edit / Delete unless the user holds students:create / :update / :delete. */
  permission?:     string;
}

export function CrudPage<T extends { _id?: string }>({
  title, subtitle,
  data, loading, meta, onSearch, onPageChange, onCreate, onUpdate, onDelete,
  columns, renderForm, defaultForm = {}, searchable = true,
  extraFilters, emptyIcon, emptyMessage, onCreateClick, onEditClick, onViewClick, actions,
  addLabel, page, totalPages, totalRecords, permission,
}: CrudPageProps<T>) {
  const can = usePermission();
  const canCreate = !permission || can(`${permission}:create`);
  const canUpdate = !permission || can(`${permission}:update`);
  const canDelete = !permission || can(`${permission}:delete`);
  const [search,      setSearch]      = useState('');
  const [modalOpen,   setModalOpen]   = useState(false);
  const [deleteModal, setDeleteModal] = useState<T | null>(null);
  const [editItem,    setEditItem]    = useState<T | null>(null);
  const [formData,    setFormData]    = useState<Partial<T>>(defaultForm);
  const [saving,      setSaving]      = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = (val: string) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { onSearch(val); }, 400);
  };

  const openCreate = () => { setEditItem(null); setFormData(defaultForm); setModalOpen(true); };
  const openEdit   = (item: T) => { setEditItem(item); setFormData(item); setModalOpen(true); };

  const handleSave = async () => {
    if (!onCreate || !onUpdate) return;
    setSaving(true);
    try {
      let success = false;
      if (editItem?._id) {
        success = await onUpdate(editItem._id, formData);
      } else {
        success = await onCreate(formData);
      }
      if (success) {
        setModalOpen(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal?._id) return;
    setDeleting(true);
    try {
      const success = await onDelete(deleteModal._id);
      if (success) {
        setDeleteModal(null);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleFieldChange = (field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const hasRowActions = !!onViewClick || canUpdate || canDelete;
  const colsWithActions: Column<T>[] = !hasRowActions ? columns : [
    ...columns,
    {
      key: '_actions' as keyof T | string,
      label: 'Actions',
      width: onViewClick ? '150px' : '130px',
      render: (_: unknown, row: T) => (
        <div style={styles.actionGroupRight}>
          {onViewClick && (
            <button
              className="icon-btn"
              title="View"
              onClick={() => onViewClick(row)}
              style={styles.viewBtnIcon}
            >
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx={12} cy={12} r={3} />
              </svg>
            </button>
          )}
          {canUpdate && (
          <button
            className="icon-btn"
            title="Edit"
            onClick={() => onEditClick ? onEditClick(row) : openEdit(row)}
            style={styles.editBtnIcon}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          )}
          {canDelete && (
          <button
            className="icon-btn"
            title="Delete"
            onClick={() => setDeleteModal(row)}
            style={styles.deleteBtnIcon}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
            </svg>
          </button>
          )}
        </div>
      ),
    },
  ];

  const effectiveMeta: PaginationMeta | null = meta || (
    page && totalPages ? { page, total_pages: totalPages, total: totalRecords || 0, page_size: 10 } : null
  );

  return (
    <div className="page-enter">
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          actions || (canCreate &&
            <button className="btn btn-primary" onClick={onCreateClick ? onCreateClick : openCreate}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M12 5v14M5 12h14"/></svg>
              {addLabel || `Add ${title.replace(/s$/, '')}`}
            </button>
          )
        }
      />

      <div className="card" style={styles.crudCardContainer}>
        {/* Toolbar */}
        <div style={styles.crudToolbar}>
          <div style={styles.crudSearchGroup}>
            {searchable && (
              <div className="search-box" style={{ width: 280 }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#9092A9" strokeWidth={2}><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"/></svg>
                <input
                  placeholder={`Search ${title.toLowerCase()}…`}
                  value={search}
                  onChange={e => handleSearch(e.target.value)}
                />
              </div>
            )}
            {extraFilters}
          </div>

          {effectiveMeta && (
            <span style={{ fontSize: '0.75rem', color: '#9092A9' }}>
              {effectiveMeta.total} total records
            </span>
          )}
        </div>

        <DataTable
          columns={colsWithActions}
          data={data}
          loading={loading}
          meta={effectiveMeta || undefined}
          onPageChange={onPageChange}
          emptyIcon={emptyIcon}
          emptyMessage={emptyMessage}
        />
      </div>

      {/* Create / Edit Modal */}
      {renderForm && (
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editItem ? `Edit ${title.replace(/s$/, '')}` : `Add New ${title.replace(/s$/, '')}`}
          size="md"
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : (editItem ? 'Update' : 'Create')}
              </button>
            </>
          }
        >
          {renderForm(formData, handleFieldChange, editItem ? 'edit' : 'create')}
        </Modal>
      )}

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete this record? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleting}
      />
    </div>
  );
}
